import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tokenRef = useRef(new URLSearchParams(window.location.search).get("token"));
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Retire immédiatement le jeton de l'URL visible (sécurité : logs, Referer, historique).
    if (tokenRef.current) window.history.replaceState({}, document.title, "/reset-password");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 8) { setError(t("reset.errShort")); return; }
    if (pwd !== confirm) { setError(t("reset.errMismatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token: tokenRef.current, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || t("reset.errGeneric"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f8fafc] px-4">
      <div className="absolute right-4 top-4"><LanguageSwitcher /></div>
      <div className="w-full max-w-sm animate-fade-up" data-testid="reset-password-page">
        <p className="overline text-[#C6A87C]">RADAR</p>
        {!tokenRef.current ? (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="reset-invalid">
            <p className="text-sm text-red-600">{t("reset.invalidLink")}</p>
            <Link to="/forgot-password" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#0F172A] underline">
              {t("reset.title")}
            </Link>
          </div>
        ) : done ? (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="reset-success">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
            <h1 className="mt-4 font-display text-2xl text-[#0F172A]">{t("reset.successTitle")}</h1>
            <p className="mt-2 text-sm text-slate-500">{t("reset.successDesc")}</p>
            <Link to="/login" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#0F172A] underline">
              <ArrowLeft className="h-4 w-4" /> {t("reset.goLogin")}
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4" data-testid="reset-password-form">
            <h1 className="font-display text-3xl tracking-tight text-[#0F172A]">{t("reset.newTitle")}</h1>
            <p className="mt-2 text-sm text-slate-500">{t("reset.newSubtitle")}</p>
            <div className="mt-6 space-y-4">
              <div>
                <Label htmlFor="pwd" className="text-slate-700">{t("reset.passwordLabel")}</Label>
                <Input id="pwd" type="password" required value={pwd} data-testid="reset-password-input"
                  onChange={(e) => setPwd(e.target.value)} className="mt-1.5 h-11" />
              </div>
              <div>
                <Label htmlFor="confirm" className="text-slate-700">{t("reset.confirmLabel")}</Label>
                <Input id="confirm" type="password" required value={confirm} data-testid="reset-confirm-input"
                  onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 h-11" />
              </div>
            </div>
            {error && <p className="mt-4 text-sm text-red-600" data-testid="reset-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="reset-submit"
              className="mt-6 h-11 w-full rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("reset.saveBtn")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
